import React, { useMemo } from 'react';
import { Grid, Typography } from '@mui/material';
import { useFormikContext } from 'formik';
import { useNavigate } from 'react-router-dom';
import { paths } from '@/app/paths';
import { AdminDataGrid } from '@/admin/shared/AdminDataGrid';
import { useAdminConfirm } from '@/admin/shared/AdminConfirmProvider';
import { useListAllUsersQuery } from '@/admin/users/api/usersApi';
import { userColumns } from '@/admin/users/UsersTable/columns';
import { useGetBrokerGroupQuery } from '@/admin/brokerGroups/api/brokerGroupsApi';

// The users table's columns this one shows, so the two look the same
const COLUMN_FIELDS = [
	'name',
	'isActive',
	'email',
	'primaryBrokerGroup',
	'otherBrokerGroups',
	'lastUpdated',
];
const columns = userColumns.filter(({ field }) => COLUMN_FIELDS.includes(field));

/**
 * The broker group edit drawer's Users section: everyone in this broker group, as their
 * primary or one of their others (from the cached users list). Users with it as their
 * primary come first, then the others, each alphabetically; a column sort overrides that.
 * Clicking a row opens that user's drawer on the users page, closing this one; with unsaved
 * changes here, the admin is asked to discard them first.
 */
export const UserFields = ({ brokerGroupId }) => {
	const navigate = useNavigate();
	const confirm = useAdminConfirm();
	const { dirty } = useFormikContext();
	const { data: users = [], isLoading, isError } = useListAllUsersQuery();
	const { data: brokerGroup } = useGetBrokerGroupQuery(brokerGroupId);

	const rows = useMemo(
		() =>
			users
				.filter((user) => user.brokerGroups.some(({ id }) => id === brokerGroupId))
				.sort(
					(a, b) =>
						Number(b.primaryBrokerGroupId === brokerGroupId) -
							Number(a.primaryBrokerGroupId === brokerGroupId) ||
						(a.name ?? '').localeCompare(b.name ?? ''),
				),
		[users, brokerGroupId],
	);

	const handleRowClick = async ({ id }) => {
		if (dirty) {
			const confirmed = await confirm({
				title: `Discard changes to ${brokerGroup.name}?`,
				content: 'Your changes to this broker group have not been saved.',
				confirmText: 'Discard',
			});
			if (!confirmed) return;
		}
		navigate(`${paths.admin}/users/${id}`);
	};

	return (
		<>
			<Grid size={12}>
				<Typography variant='form_label'>Users</Typography>
			</Grid>
			<Grid size={12}>
				<AdminDataGrid
					table='brokerGroupUsers'
					columns={columns}
					rows={rows}
					isLoading={isLoading}
					isError={isError}
					objectType='users'
					maxHeight='400px'
					initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
					onRowClick={handleRowClick}
				/>
			</Grid>
		</>
	);
};
